import { createSelector as createMemoizedSelector, Selector } from 'reselect';

import { selectSpaceInfo } from '~src/spacesPlatform/selectors/SpaceInfoSelectors';

import { AppState } from '../../bootstrap/redux/state/AppState';
import { doesSpaceSupportStylishFiltering } from '../actions/filters/FilterHelpers';
import { ContentFiltersKeys } from '../models/ContentFilters';
import { getKeyForExplorableItem, SpaceInfoItemType } from '../models/ContentType';
import { IExplorableItemTypeInfo } from '../models/resourceInfoParts';
import { ContentExplorerToolbarState } from '../state/ContentExplorerState';
import { isFavoritesSpace, isRecommendationsSpace, SpaceInfo } from '../state/SpaceInfo';
import { ItemsSet, makeItemsSet } from '../utils/ItemsSet';
import { selectSpecificMixedContentFilterClause } from './ContentFilterSelectors';

// Meant for use with request missing slice in this case we don't want to filter out null items.
// Null items are items we've yet to fetch from server.
export function selectContentFromAppStateWithoutFilters(state: AppState): ReadonlyArray<IExplorableItemTypeInfo> {
  return state.contentExplorer.content.contentItems;
}

export function selectContentFromAppState(state: AppState): ReadonlyArray<IExplorableItemTypeInfo> {
  const currSpaceInfo = selectSpaceInfo(state);
  const favoriteFilterApplied = selectSpecificMixedContentFilterClause(state, ContentFiltersKeys.favorite);
  const removeFavoriteFromExplorer = favoriteFilterApplied && doesSpaceSupportStylishFiltering(currSpaceInfo);

  if (isFavoritesSpace(currSpaceInfo) || removeFavoriteFromExplorer) {
    return selectFavoritedContentItems(state);
  } else if (isRecommendationsSpace(currSpaceInfo)) {
    return filterHiddenContentItems(state);
  } else {
    return state.contentExplorer.content.contentItems;
  }
}

export function isInCurrentSpace(state: AppState, space: {spaceType: SpaceInfoItemType, id: string }) {
  const currSpaceInfo = selectSpaceInfo(state);
  return currSpaceInfo.item.id === space.id && currSpaceInfo.spaceType === space.spaceType;
}

export function selectContentTotalLength(state: AppState): number {
  const currSpaceInfo: SpaceInfo = selectSpaceInfo(state);
  if (currSpaceInfo.isKeysetPaginationSpace) {
    return state.contentExplorer.content.totalCount;
  }
  return state.contentExplorer.content.contentItems.length;
}

export function selectContentNextPageToken(state: AppState): string {
  return state.contentExplorer.content.contentNextPageToken;
}

export function selectMoreItemsForTokenRequest(state: AppState): boolean {
  return state.contentExplorer.content.moreItemsForTokenRequest;
}

export function selectContentExplorerSelectionIsBusy(state: AppState): boolean {
  return state.contentExplorer.selection.isBusy;
}

export function selectContentExplorerToolbarState(state: AppState): ContentExplorerToolbarState {
  return state.contentExplorer.toolbar;
}

function selectAllSelectedContentItems(state: AppState): ItemsSet<IExplorableItemTypeInfo> {
  return state.contentExplorer.selection.selectedItems;
}

function filterHiddenContentItems(state: AppState): ReadonlyArray<IExplorableItemTypeInfo> {
  const unhiddenItems = state.contentExplorer.content.contentItems
  .filter((item) => (item as any).hide === false);

  return unhiddenItems;
}

/**
 * Filters out the selected items with respect to the actual content items, to make sure
 * we don't still select items that are no longer in our list.
 *
 * The actions menu from the explorer toolbar takes in the selected items set as its target items,
 * rather than the actual items from the content explorer that correspond to these selected keys.
 * This can be removed when we update all sagas to use IContentTypeItemInfo instead of IContentInfo
 * (see TFS tech debt story 808815).
 */
export const selectSelectedContentItems: Selector<AppState, ItemsSet<IExplorableItemTypeInfo>> =
  createMemoizedSelector([selectContentFromAppState, selectAllSelectedContentItems],
    (contentItems, allSelectedContentItems) => {
      const filteredSelectedItems = contentItems
        // W-14678346 - array might contain undefined items when there are workbook drafts
        .filter(c => !!c)
        .filter(c => {
          const key = getKeyForExplorableItem(c);
          return allSelectedContentItems.hasOwnProperty(key);
        });
      return makeItemsSet(getKeyForExplorableItem, ...filteredSelectedItems);
    });

function selectAllContentItems(state: AppState): ReadonlyArray<IExplorableItemTypeInfo> {
  return state.contentExplorer.content.contentItems;
}
export const selectFavoritedContentItems: Selector<AppState, ReadonlyArray<IExplorableItemTypeInfo>> =
  createMemoizedSelector([selectAllContentItems], (items) => {
    return items.filter((item) => (item as any).favorite);
  });
